import { axiosClient } from "@/shared/api/axios-client";
import type { Paginado } from "@/lib/types/common";

/** Gestión Documental · fase 3 · estados de revisión de un documento (null = aún no evaluado). */
export type EstadoRevisionDocumento = "NoRequiere" | "PendienteDeRevision" | "Aprobado" | "Observado";
export type AccionObservacion = "DevolverACorreccion" | "Bloquear";

/** Estado de revisión de un documento tal como lo pintan las filas del paso «Documentos». */
export interface RevisionResumen {
  estado: EstadoRevisionDocumento | null;
  accion: AccionObservacion | null;
  comentario: string | null;
  /** Fase 4 · vigencia (sin fecha de emisión = no vence). */
  manejaVigencia?: boolean;
  fechaEmision?: string | null;
  fechaVencimiento?: string | null;
  vencido?: boolean;
}

export interface DocumentoEnRevision {
  documentoId: string;
  solicitudId: string;
  numeroSolicitud: number;
  folio: string;
  organizacionId: string | null;
  subtipo: string | null;
  categoria: string;
  categoriaNombre: string;
  tipoDocumento: string | null;
  nombreOriginal: string;
  extension: string;
  versionActual: number;
  estadoRevision: EstadoRevisionDocumento | null;
  accionObservacion: AccionObservacion | null;
  comentario: string | null;
  rolesRevisores: string[];
  cargadoAt: string;
  /** Fase 4 · vencimiento (null = sin vigencia o sin fecha de emisión). */
  fechaVencimiento?: string | null;
  vencido?: boolean;
}

/** Bandeja paginada · `puedeRevisar` = el usuario revisa alguna categoría/tipo (o revisa todo). */
export interface BandejaRevision extends Paginado<DocumentoEnRevision> {
  puedeRevisar: boolean;
}

export interface ListarBandejaRevisionParams {
  categoria?: string;
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

export interface RevisionDeDocumento {
  version: number;
  resultado: "Aprobado" | "Observado";
  accion: AccionObservacion | null;
  comentario: string | null;
  revisorId: string;
  revisorNombre: string | null;
  revisadoAt: string;
}

export interface VersionEnExpediente {
  numero: number;
  nombreOriginal: string;
  tamanoBytes: number;
  cargadoAt: string;
  subidoPorUsuarioId: string;
  subidoPorNombre: string | null;
  motivo: string | null;
  esVigente: boolean;
  fechaEmision?: string | null;
}

export interface DocumentoEnExpediente {
  documentoId: string;
  numeroCfdi: number;
  categoria: string;
  categoriaNombre: string;
  tipoDocumento: string | null;
  nombreOriginal: string;
  extension: string;
  tamanoBytes: number;
  versionActual: number;
  esReemplazable: boolean;
  estadoRevision: EstadoRevisionDocumento | null;
  accionObservacion: AccionObservacion | null;
  comentarioRevision: string | null;
  rolesRevisores: string[];
  puedoRevisar: boolean;
  versiones: VersionEnExpediente[];
  revisiones: RevisionDeDocumento[];
  /** Fase 4 · vigencia y si el vencimiento impide aprobarlo en revisión. */
  manejaVigencia: boolean;
  fechaEmision: string | null;
  fechaVencimiento: string | null;
  vencido: boolean;
  aprobacionBloqueadaPorVencimiento: boolean;
}

export interface AccesoEnExpediente {
  documentoId: string;
  nombreOriginal: string;
  accion: string;
  usuarioId: string;
  usuarioNombre: string | null;
  ocurridoAt: string;
  detalle: string | null;
}

export interface Expediente {
  solicitudId: string;
  numeroSolicitud: number;
  folio: string;
  estado: string;
  esSolicitante: boolean;
  documentos: DocumentoEnExpediente[];
  accesos: AccesoEnExpediente[];
}

export interface RevisarDocumentoBody {
  aprobar: boolean;
  accion?: AccionObservacion | null;
  comentario?: string | null;
}

export async function listarBandejaRevision(params: ListarBandejaRevisionParams = {}): Promise<BandejaRevision> {
  const { data } = await axiosClient.get<BandejaRevision>("/api/revision-documental", { params });
  return data;
}

export async function contarRevisionPendientes(): Promise<{ puedeRevisar: boolean; pendientes: number }> {
  const { data } = await axiosClient.get<{ puedeRevisar: boolean; pendientes: number }>("/api/revision-documental/pendientes/count");
  return data;
}

export async function obtenerExpediente(solicitudId: string): Promise<Expediente> {
  const { data } = await axiosClient.get<Expediente>(`/api/solicitudes/${solicitudId}/expediente`);
  return data;
}

export async function revisarDocumento(solicitudId: string, documentoId: string, body: RevisarDocumentoBody): Promise<void> {
  await axiosClient.post(`/api/solicitudes/${solicitudId}/documentos/${documentoId}/revision`, body);
}
